import React, { Fragment } from 'react';
import * as PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { INSUFFICIENT_EVIDENCE } from '../utils/Consts';
import './Scoring.css';
import { Button, Tooltip } from '@mui/material';
import { CSVLink } from 'react-csv';
import { getCsvData } from '../utils/CSV';
import FileDownload from '@mui/icons-material/FileDownload';
import ContentCopy from '@mui/icons-material/ContentCopy';

function Scoring({ t, results, globalAssessmentState, graphicsLayer }) {
  const {
    adaptiveCapacityInfo,
    exposureResultInfos,
    moduleCompleteness
  } = results;

  const hasGraphcis = graphicsLayer.graphics.length > 0;

  let alerts = [];
  if (!moduleCompleteness.A) {
    alerts.push(<Alert key='climateExposure' severity='error'>{t('scoring.noClimateExposure')}</Alert>);
  }

  if (!moduleCompleteness.climateExposureModifier) {
    alerts.push(<Alert key='climateExposureModifier' severity='error'>{t('scoring.noClimateExposureModifier')}</Alert>);
  }

  if (!moduleCompleteness.adaptiveCapacity) {
    alerts.push(<Alert key='adaptiveCapacity' severity='error'>{t('scoring.noAdaptiveCapacity')}</Alert>);
  }

  if (!moduleCompleteness.adaptiveCapacityModifier) {
    alerts.push(<Alert key='adaptiveCapacityModifier' severity='error'>{t('scoring.noAdaptiveCapacityModifier')}</Alert>);
  }

  const adaptiveCapacityTooltip = adaptiveCapacityInfo.score === INSUFFICIENT_EVIDENCE ?
    t('scoring.noAdaptiveCapacity')
    : adaptiveCapacityInfo.score.toFixed(2);

  return <>
    {alerts.length !== 0
      ? <Stack>
        {alerts}
      </Stack>
      : null}
    <TableContainer component={Paper}>
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableBody>
          <TableRow>
            <TableCell align='right' sx={{ fontWeight: 'bold' }}>{t('scoring.adaptiveCapacity')}:</TableCell>
            <TableCell align='left'>
              <Tooltip title={adaptiveCapacityTooltip}>
                <span>{t(`scoring.${adaptiveCapacityInfo.categoryKey}`)}</span>
              </Tooltip>
            </TableCell>
          </TableRow>
          {exposureResultInfos.map(({
            climateExposure,
            climateExposureCategory,
            traitScore,
            overallCategory,
            moduleKCategory,
            rcpKey
          }) => {
            const climateExposureTooltip = climateExposure === INSUFFICIENT_EVIDENCE ?
              t('scoring.noClimateExposure')
              : climateExposure.toFixed(2);

            const overallTooltip = traitScore !== INSUFFICIENT_EVIDENCE && (
              <div>
                <div>{`${t('scoring.vulnerabilitySubscore')}: ${traitScore.toFixed(2)}`}</div>
                <div>{`${t('scoring.moduleKCategory')}: ${moduleKCategory}`}</div>
              </div>
            );
            return (
              <Fragment key={rcpKey}>
                <TableRow>
                  <TableCell align='center' colSpan={2} sx={{ fontWeight: 'bold' }}>
                   {t(`scoring.${rcpKey}`)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right' sx={{ fontWeight: 'bold' }}>{t('scoring.exposure')}:</TableCell>
                  <TableCell align='left'>
                    <Tooltip title={climateExposureTooltip}>
                      <span>{t(`scoring.${climateExposureCategory}`)}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell align='right' sx={{ fontWeight: 'bold' }}>{t('scoring.overallCategory')}:</TableCell>
                  <TableCell align='left'>
                    <Tooltip title={overallTooltip} >
                      <span>{t(`scoring.${overallCategory}`)}</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer >
    <div className='scoring-actions'>
      <CSVLink
        data={getCsvData(results, globalAssessmentState, hasGraphcis, t)}
        filename="ccvi_data.csv"
      >
        <Button component="span" variant='contained' startIcon={<FileDownload />}>
          {t('scoring.export')}
        </Button>
      </CSVLink>
      <Button
        variant='contained'
        startIcon={<ContentCopy />}
        onClick={() => {
          const values = getCsvData(results, globalAssessmentState, hasGraphcis, t)[1];
          const tabSeparatedVals = values.join('\t');
          navigator.clipboard.writeText(tabSeparatedVals);
        }}
      >
        {t('scoring.copyToClipboard')}
      </Button>
    </div>
  </>;
}

Scoring.propTypes = {
  t: PropTypes.func.isRequired,
  results: PropTypes.shape({
    adaptiveCapacityInfo: PropTypes.shape({
      score: PropTypes.number,
      categoryKey: PropTypes.string
    }),
    exposureResultInfos: PropTypes.arrayOf(
      PropTypes.shape({
        climateExposure: PropTypes.number,
        traitScore: PropTypes.number,
        climateExposureCategory: PropTypes.string,
        overallCategory: PropTypes.string,
        moduleKCategory: PropTypes.string,
        rcpKey: PropTypes.string
      })
    ),
    moduleCompleteness: PropTypes.objectOf(
      /**
       * Percentage complete for module keys (string for formatted floats, number for 0 or 100)
       * And boolean for aggregate completion statuses
       **/
      PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool])
    ).isRequired
  }),
  globalAssessmentState: PropTypes.object.isRequired,
  graphicsLayer: PropTypes.object.isRequired
};

export default withTranslation('translations')(Scoring);
